import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Stack
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Inventory as InventoryIcon,
  Checkroom as FashionIcon,
  RequestQuote as ValorizacionIcon,
  QrCode2 as LoteIcon,
  AccountBalanceWallet as SaldosIcon,
  Timeline as TrazabilidadIcon,
  EventBusy as VencimientosIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import MisInformesHubSection from '../../components/shared/MisInformesHubSection';
import { usePermissions } from '../../contexts/PermissionContext';

const Informes: React.FC = () => {
  const navigate = useNavigate();
  const { canModule } = usePermissions();
  const fashionHabilitado = canModule('fashion');

  const informes = [
    {
      title: 'Stock por Depósito',
      description: 'Visualice el stock de productos por cada depósito.',
      icon: InventoryIcon,
      path: 'stock-deposito',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Stock valorizado a fecha',
      description: 'Maestro de artículos con stock a fecha valorizado al costo neto actual.',
      icon: ValorizacionIcon,
      path: 'stock-a-fecha',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Stock inmovilizado',
      description: 'Artículos con stock que no se vendieron en el período seleccionado.',
      icon: InventoryIcon,
      path: 'stock-inmovilizado',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Movimientos de Stock',
      description: 'Consulte el historial de movimientos de stock.',
      icon: AssessmentIcon,
      path: 'movimientos-stock',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    ...(fashionHabilitado
      ? [{
          title: 'Curva de talles',
          description: 'Stock por modelo, color y talle con marca de quiebre.',
          icon: FashionIcon,
          path: 'curva-talles',
          color: '#db2777',
          bgGradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
          iconBg: 'rgba(219, 39, 119, 0.1)'
        }]
      : []),
    {
      title: 'Movimientos de lotes',
      description: 'Historial de ingresos y egresos por lote o serie.',
      icon: LoteIcon,
      path: 'lotes-movimientos',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Saldos de lotes',
      description: 'Saldo disponible por lote/serie y depósito.',
      icon: SaldosIcon,
      path: 'lotes-saldos',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Trazabilidad / recall',
      description: 'Seguimiento de un lote hacia clientes y producción.',
      icon: TrazabilidadIcon,
      path: 'lotes-trazabilidad',
      color: '#1976d2',
      bgGradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      iconBg: 'rgba(25, 118, 210, 0.1)'
    },
    {
      title: 'Vencimientos de lotes',
      description: 'Lotes vencidos o próximos a vencer con saldo.',
      icon: VencimientosIcon,
      path: 'lotes-vencimientos',
      color: '#ed6c02',
      bgGradient: 'linear-gradient(135deg, #ff9800 0%, #ed6c02 100%)',
      iconBg: 'rgba(237, 108, 2, 0.1)'
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2',
                mb: 1
              }}
            >
              Informes de Inventario
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.95rem' }}
            >
              Selecciona un informe para visualizar información detallada
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Informes estándar
          </Typography>
          <Grid container spacing={2.5}>
            {informes.map((informe) => {
              const IconComponent = informe.icon;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={informe.path}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #e0e0e0',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      height: '100%',
                      minHeight: { xs: 180, sm: 195 },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                        borderColor: informe.color,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(informe.path)}
                      sx={{
                        p: 0,
                        height: '100%',
                        alignItems: 'stretch',
                        '&:hover': {
                          '& .MuiCardActionArea-focusHighlight': {
                            opacity: 0,
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.25 }}>
                        <Stack spacing={1.5}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: informe.iconBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IconComponent
                              sx={{
                                fontSize: 27,
                                color: informe.color,
                              }}
                            />
                          </Box>

                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 600,
                              color: '#1a1a1a',
                              mb: 0.5,
                            }}
                          >
                            {informe.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.975rem',
                              lineHeight: 1.6,
                            }}
                          >
                            {informe.description}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: informe.color,
                              fontWeight: 500,
                              fontSize: '0.85rem',
                            }}
                          >
                            Ver informe
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                transition: 'transform 0.3s ease',
                                display: 'inline-block',
                                '&:hover': {
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              →
                            </Box>
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <MisInformesHubSection
            titulo="Mis informes de inventario"
            extraCards={[
              {
                title: 'Store de informes',
                description: 'Instalá informes de la biblioteca para usarlos con los datos de la empresa.',
                icon: StorefrontIcon,
                path: 'personalizados?seccion=store',
              },
            ]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Informes;
